import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles, Plus, Search } from "lucide-react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import { dictionary, Language } from "@/utils/dictionary";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Step1SelectProductProps {
  language: Language;
  onClose: () => void;
  onSelectProduct: (product: ProductInfoCreative) => void;
  onCreateNew?: () => void;
}

export function Step1SelectProduct({
  language,
  onClose,
  onSelectProduct,
  onCreateNew,
}: Step1SelectProductProps) {
  const [products, setProducts] = useState<ProductInfoCreative[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const data = await creativesService.listProducts();
        setProducts(data);
      } catch (error) {
        console.error("Error loading products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) => p.name?.toLowerCase().includes(q));
  }, [products, search]);

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] || fallback;

  return (
    <>
      {/* Header */}
      <div className="p-6 pb-4 text-center border-b border-border/30">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
          style={{
            background: "linear-gradient(135deg, #4368E5 0%, #4368E5 100%)",
          }}
        >
          <Sparkles className="h-7 w-7 text-white" />
        </motion.div>

        <h2 className="text-xl font-bold text-foreground mb-1">
          {t("select-a-product", "Selecciona un Producto")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            "select-the-product-for-which-you-want-to-create-the-ad",
            "¿Para cuál producto quieres crear el anuncio?"
          )}
        </p>
      </div>

      {/* Product List */}
      <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">
              {t("loading-products", "Cargando productos...")}
            </div>
          </div>
        ) : (
          <>
            {/* TODO: Habilitar cuando el backend soporte POST /ads/creatives/products/{productId} */}
            {/* Create New Product — oculto temporalmente */}

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search-product", "Buscar producto...")}
                className={cn(
                  "w-full pl-9 pr-3 py-2.5 rounded-xl text-sm",
                  "bg-muted/30 border border-border/50",
                  "text-foreground placeholder:text-muted-foreground/50",
                  "focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20",
                  "transition-colors"
                )}
              />
            </div>

            {filteredProducts.length === 0 && !onCreateNew ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">
                  {t("no-products-available", "No hay productos disponibles")}
                </div>
              </div>
            ) : filteredProducts.length === 0 && search.trim() ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground/60">
                  {t("no-results", "Sin resultados para")} &ldquo;{search}&rdquo;
                </p>
              </div>
            ) : (
              filteredProducts.map((product, index) => (
                <motion.button
                  key={`${product.product_id}-${product.website_id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: (onCreateNew ? 0.1 : 0) + index * 0.05,
                  }}
                  onClick={() => onSelectProduct(product)}
                  type="button"
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl",
                    "border border-border/50 bg-card",
                    "hover:border-primary/50 hover:bg-primary/5",
                    "transition-all duration-200 text-left group"
                  )}
                >
                  <div className="w-12 h-12 rounded-lg bg-white border border-border/30 overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground text-sm line-clamp-1 group-hover:text-primary transition-colors">
                      {product.name || t("product", "Producto")}
                    </h3>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <svg
                      className="h-4 w-4 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </motion.button>
              ))
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 pt-0">
        <Button
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          {t("cancel", "Cancelar")}
        </Button>
      </div>
    </>
  );
}
